import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import { authGet, authPost, authPut, authDelete } from "../../lib";

const useStyles = makeStyles((theme) => ({
  root: { maxWidth: 900, width: "100%", margin: "0 auto", padding: theme.spacing(3) },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  filterInput: { width: 260 },
  termCell: { width: "38%" },
  defCell: { width: "44%" },
  editCell: { width: 56, padding: "0 4px" },
  deleteCell: { width: 56, padding: "0 4px" },
  emptyCell: { color: "#999", padding: theme.spacing(3), textAlign: "center" },
}));

export function AliasPage() {
  const classes = useStyles();

  const [aliases, setAliases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");

  const [newTerm, setNewTerm] = useState("");
  const [newDef, setNewDef] = useState("");
  const [adding, setAdding] = useState(false);

  const [editId, setEditId] = useState(null);
  const [editTerm, setEditTerm] = useState("");
  const [editDef, setEditDef] = useState("");

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const [snack, setSnack] = useState({ open: false, msg: "" });

  useEffect(() => {
    fetchAliases();
  }, []);

  async function fetchAliases() {
    setLoading(true);
    const { data, error } = await authGet("/search-aliases");
    setLoading(false);
    if (error) { showSnack(error?.message || "Failed to load aliases"); return; }
    setAliases(data || []);
  }

  function showSnack(msg) {
    setSnack({ open: true, msg });
  }

  async function handleAdd() {
    const term = newTerm.trim();
    const definition = newDef.trim();
    if (!term || !definition) return;
    setAdding(true);
    const { data, error } = await authPost("/search-aliases", { term, definition });
    setAdding(false);
    if (error) { showSnack(error?.message || "Failed to add alias"); return; }
    setAliases((prev) => [...prev, data].sort((a, b) => a.term.localeCompare(b.term)));
    setNewTerm("");
    setNewDef("");
    showSnack("Alias added");
  }

  function startEdit(alias) {
    setEditId(alias.alias_id);
    setEditTerm(alias.term);
    setEditDef(alias.definition);
  }

  function cancelEdit() {
    setEditId(null);
  }

  async function handleSaveEdit() {
    const term = editTerm.trim();
    const definition = editDef.trim();
    if (!term || !definition) return;
    const { data, error } = await authPut(`/search-aliases/${editId}`, { term, definition });
    if (error) { showSnack(error?.message || "Failed to update alias"); return; }
    setAliases((prev) =>
      prev.map((a) => (a.alias_id === editId ? data : a)).sort((a, b) => a.term.localeCompare(b.term))
    );
    setEditId(null);
    showSnack("Alias updated");
  }

  async function handleDelete() {
    const { error } = await authDelete(`/search-aliases/${confirmDeleteId}`);
    if (error) { showSnack(error?.message || "Failed to delete alias"); return; }
    setAliases((prev) => prev.filter((a) => a.alias_id !== confirmDeleteId));
    setConfirmDeleteId(null);
    showSnack("Alias deleted");
  }

  const filterLower = filter.toLowerCase();
  const visible = filter
    ? aliases.filter(
        (a) =>
          a.term.toLowerCase().includes(filterLower) ||
          a.definition.toLowerCase().includes(filterLower)
      )
    : aliases;

  const deleteTarget = aliases.find((a) => a.alias_id === confirmDeleteId);

  return (
    <Box className={classes.root}>
      <Typography variant="h6" gutterBottom>Search Aliases</Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
        When a user searches for a <strong>term</strong>, it is replaced by its <strong>definition</strong> before querying.
      </Typography>

      <Box className={classes.toolbar}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Filter aliases…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className={classes.filterInput}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" style={{ color: "#aaa" }} />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" color="textSecondary">
          {visible.length} of {aliases.length} alias{aliases.length !== 1 ? "es" : ""}
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.termCell}>Term</TableCell>
              <TableCell className={classes.defCell}>Definition</TableCell>
              <TableCell className={classes.editCell} />
              <TableCell className={classes.deleteCell} />
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Add row */}
            <TableRow>
              <TableCell>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="New term"
                  value={newTerm}
                  onChange={(e) => setNewTerm(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Definition / replacement"
                  value={newDef}
                  onChange={(e) => setNewDef(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                />
              </TableCell>
              <TableCell colSpan={2}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={adding || !newTerm.trim() || !newDef.trim()}
                  onClick={handleAdd}
                >
                  Add
                </Button>
              </TableCell>
            </TableRow>

            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" style={{ padding: 24 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className={classes.emptyCell}>
                  {filter ? "No matching aliases" : "No aliases yet"}
                </TableCell>
              </TableRow>
            ) : (
              visible.map((a) => (
                <TableRow key={a.alias_id} hover>
                  <TableCell>
                    {editId === a.alias_id ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={editTerm}
                        onChange={(e) => setEditTerm(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") handleSaveEdit(); if (e.key === "Escape") cancelEdit(); }}
                        autoFocus
                      />
                    ) : (
                      a.term
                    )}
                  </TableCell>
                  <TableCell>
                    {editId === a.alias_id ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={editDef}
                        onChange={(e) => setEditDef(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") handleSaveEdit(); if (e.key === "Escape") cancelEdit(); }}
                      />
                    ) : (
                      a.definition
                    )}
                  </TableCell>
                  <TableCell className={classes.editCell} align="center">
                    {editId === a.alias_id ? (
                      <IconButton size="small" onClick={handleSaveEdit} title="Save">
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <IconButton size="small" onClick={() => startEdit(a)} title="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell className={classes.deleteCell} align="center">
                    {editId === a.alias_id ? (
                      <IconButton size="small" onClick={cancelEdit} title="Cancel">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <IconButton size="small" onClick={() => setConfirmDeleteId(a.alias_id)} title="Delete">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)}>
        <DialogTitle>Delete Alias</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete alias <strong>{deleteTarget?.term}</strong> → <strong>{deleteTarget?.definition}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="secondary">Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}
        message={snack.msg}
      />
    </Box>
  );
}